import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Box, Tab, Typography, IconButton, Divider, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import InfoIcon from '@mui/icons-material/Info'
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { Helmet } from 'react-helmet-async'
import { useProject } from "../context/ProjectContext"
import { useNotification } from '../context/NotificationContext'
import ProjectService from '../services/ProjectService'
import PageService from '../services/PageService'
import ScanService from '../services/ScanService'
import { socketEventEmitter } from '../socket'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import BreadcrumbsNav from '../components/BreadcrumbsNav'
import UiTestingTab from '../components/discovery/tabs/uiTesting/UiTestingTab'
import TestCasesTab from '../components/discovery/tabs/testCases/TestCasesTab'
import OverviewTab from '../components/discovery/tabs/overview/OverviewTab'
import PageLink from 'components/common/PageLink'
import usePageTasksStatus from 'hooks/usePageTasksStatus'

const Discovery = () => {
  const location = useLocation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { selectedProject, setSelectedProject } = useProject()
  const showNotification = useNotification()

  const [activeTabValue, setActiveTabValue] = useState('1')
  const [page, setPage] = useState(null)
  const [allResolutionPages, setAllResolutionPages] = useState([])
  const [selectedScreen, setSelectedScreen] = useState('desktop')
  const [discoveryPageToggler, setDiscoveryPageToggler] = useState(false)

  const { isUiScanInProgress, isGenerateTestsInProgress } = usePageTasksStatus({ page })

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const pageData = await PageService.getPageById(id)
        setPage(pageData)

        if (pageData) {
          const allPagesData = await PageService.getAllPagesByProjectId(pageData.page.project_id)
          const projectData = await ProjectService.getProjectById(pageData.page.project_id)
          if (projectData?.id && projectData.id !== selectedProject?.id) {
            setSelectedProject(projectData)
          }
          const filteredPages = allPagesData.filter(page => page.url === pageData.page.url && projectData.screen_sizes.includes(page.screen_size))
          setAllResolutionPages(filteredPages)
        }
      } catch (error) {
        console.error('Error fetching Pages:', error)
      }
    }

    fetchPageData()
  }, [id, discoveryPageToggler, selectedProject?.id, setSelectedProject])

  useEffect(() => {
    const handleTaskCompleted = (data) => {
      if (data.pageId === page?.page.id) {
        setDiscoveryPageToggler(!discoveryPageToggler)
      }
    }
    const handleTaskFailed = (data) => {
      if (data.pageId === page?.page.id) {
        setDiscoveryPageToggler(!discoveryPageToggler)
      }
    }
    const handleNewTaskAdded = (data) => {
      if (data.pageId === page?.page.id) {
        setDiscoveryPageToggler(!discoveryPageToggler)
      }
    }

    socketEventEmitter.on('taskCompleted', handleTaskCompleted)
    socketEventEmitter.on('taskFailed', handleTaskFailed)
    socketEventEmitter.on('newTaskAdded', handleNewTaskAdded)

    return () => {
      socketEventEmitter.off('taskCompleted', handleTaskCompleted)
      socketEventEmitter.off('taskFailed', handleTaskFailed)
      socketEventEmitter.off('newTaskAdded', handleNewTaskAdded)
    }
  }, [page?.page.id, discoveryPageToggler])

  useEffect(() => {
    if (page) {
      setSelectedScreen(page.page.screen_size)
    }
  }, [selectedProject?.id, page])

  useEffect(() => {
    const previousPath = sessionStorage.getItem("previousPath")

    if (previousPath?.startsWith("/test-case/")) {
      setActiveTabValue("3")
    }
  }, [])

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTabValue(location.state?.activeTab)
    }
  }, [location.state])

  const isScreenSizeAvailable = (screenSize) => {
    return allResolutionPages.some(page => page.screen_size === screenSize)
  }

  const handleScreenChange = (event) => {
    setSelectedScreen(event.target.value)
    if (event.target.value !== page.page.screen_size) {
      const targetPage = allResolutionPages.find(page => page.screen_size === event.target.value)
      navigate(`/pages/${targetPage.id}`)
    }
  }

  const handleScanPage = async () => {
    const pageIds = [page.page.id]
    try{
      await ScanService.scanPages({ pageIds });
      showNotification('UI Scan has started', 'info', 'Learn more', navigate);
    }
    catch(error){
      showNotification(error?.response?.data?.message || 'Unexpected error', 'error', 'Learn more', navigate);
    }
  }

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTabValue(newValue)
  }

  const handleOpenProjectSettings = () => {
    if (selectedProject?.id) {
      navigate(`/project/${selectedProject.id}`)
    }
  }

  return (
    <Box className="discovery rootWrapper">
      <Helmet>
        <title>{`Treegress - ${selectedProject?.name || "Project"} ${page?.page.label || "Page"}`}</title>
      </Helmet>
      <Header/>
      <Box className="srollContent">
        <Sidebar />
        <Box className="discoveryContentContainer">
          <BreadcrumbsNav pageLabel="discovery" pageName={page?.page.label}/>
          <Box className="discoveryTopSection">
            <Box className="discoveryBackButton" onClick={() => navigate('/pages')}>
              <ArrowBackIosIcon/>
            </Box>
            <Typography className='discoveryTitle'>{page?.page.label}</Typography>
          </Box>
          <PageLink link={page?.page.url} />
          <TabContext value={activeTabValue}>
            <Box className="discoveryTabsTogglerContainer">
              <TabList className="discoveryTabsToggler" onChange={handleChangeActiveTab}>
                <Tab
                  className="discoveryTabLabelContainer"
                  label={
                    <Box
                      className="discoveryTabLabel"
                      sx={{ color: activeTabValue === "1" ? "#4574D2" : "rgba(0, 0, 0, 0.6)" }}
                    >
                      <InfoIcon
                        className="discoveryTabLabelIcon"
                        color={activeTabValue === "1" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"}
                      />
                      <span className="discoveryTabLabelText">OVERVIEW</span>
                    </Box>
                  }
                  value="1"
                />
                <Tab
                  className="discoveryTabLabelContainer"
                  label={
                    <Box
                      className="discoveryTabLabel"
                      sx={{ color: activeTabValue === "2" ? "#4574D2" : "rgba(0, 0, 0, 0.6)" }}
                    >
                      <DesignServicesOutlinedIcon
                        className="discoveryTabLabelIcon"
                        color={activeTabValue === "2" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"}
                      />
                      <span className="discoveryTabLabelText">UI TESTING</span>
                    </Box>
                  }
                  value="2"
                />
                <Tab
                  className='discoveryTabLabelContainer'
                  label={
                    <Box
                      className="discoveryTabLabel"
                      sx={{ color: activeTabValue === "3" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"}}
                    >
                      <ListAltOutlinedIcon
                        className="discoveryTabLabelIcon"
                        color={activeTabValue === "3" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"}
                      />
                      <span className="discoveryTabLabelText">TEST CASES</span>
                    </Box>
                  }
                  value="3"
                />
              </TabList>
              <Box className="discoveryActions">
                { activeTabValue === "2"
                  ? <>
                    <Box className="discoveryActionsPlatformContainer">
                      <FormControl variant="outlined">
                        <InputLabel>Platform</InputLabel>
                        <Select
                          className="discoveryActionsPlatformSelect"
                          size="small"
                          value={selectedScreen}
                          onChange={handleScreenChange}
                          label="Platform"
                        >
                          <MenuItem value="desktop" disabled={!isScreenSizeAvailable('desktop')}>
                            Desktop
                          </MenuItem>
                          <MenuItem value="tablet" disabled={!isScreenSizeAvailable('tablet')}>
                            Tablet
                          </MenuItem>
                          <MenuItem value="mobile" disabled={!isScreenSizeAvailable('mobile')}>
                            Mobile
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    { page?.page?.last_scanned_at
                      ? <LoadingButton
                        id='rescanPageButton'
                        className="discoveryActionsRescanButton"
                        variant="contained"
                        color="secondary"
                        onClick={handleScanPage}
                        loading={isUiScanInProgress}
                        disabled={isUiScanInProgress}
                        loadingIndicator="SCANNING…"
                        sx={{ opacity: isUiScanInProgress ? 0.8 : 1, ml: 2 }}
                      >
                        UI RESCAN
                      </LoadingButton>
                      : <Button className="discoveryActionsRescanButton" variant="contained" disabled>
                        UI RESCAN
                      </Button>
                    }
                    <Divider orientation="vertical" flexItem/>
                  </>
                  : null
                }
                <IconButton className="discoveryActionIconButton" onClick={handleOpenProjectSettings}>
                  <SettingsOutlinedIcon color='secondary' />
                </IconButton>
              </Box>
            </Box>
            <TabPanel value="1" sx={{ p: 0 }}>
              <OverviewTab
                pageData={page}
                isScanLoading={isUiScanInProgress}
                handleScanPage={handleScanPage}
                discoveryPageToggler={discoveryPageToggler}
                setDiscoveryPageToggler={setDiscoveryPageToggler}
                isGenerateTestsInProgress={isGenerateTestsInProgress}
              />
            </TabPanel>
            <TabPanel value="2" sx={{ p: 0 }}>
              <UiTestingTab
                page={page}
                isScanLoading={isUiScanInProgress}
                handleScanPage={handleScanPage}
                isGenerateTestsInProgress={isGenerateTestsInProgress}
              />
            </TabPanel>
            <TabPanel value="3" sx={{ p: 0 }}>
              <TestCasesTab 
                isScanLoading={isUiScanInProgress} 
                pageData={page} 
                isGenerateTestsInProgress={isGenerateTestsInProgress} 
              />
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
    </Box>
  )
}

export default Discovery
